import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faClose } from '@fortawesome/pro-regular-svg-icons';

import { GetNextId } from '@eiq/shared/utils';

import { QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { SelectableVariableRequireType } from '../../../models/sources/variable-required-type.constants';
import { SelectableVariableValueType } from '../../../models/sources/variable-value-type.constants';
import { QuestionFormSourceVariable } from '../../../models/type-forms/question-form-source-variable.model';
import { QuestionOptionForm } from '../../../models/type-forms/question-option-form.model';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'empathy-source-variable-dialog',
  templateUrl: 'source-variable-dialog.component.html',
  styleUrl: './source-variable-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceVariableDialogComponent implements OnInit {
  faClose = faClose;

  listType = QuestionType.SingleChoice;
  separatorKeysCodes: number[] = [COMMA, ENTER];

  requiredTypes = SelectableVariableRequireType;
  valueTypes = SelectableVariableValueType;

  addValueCtrl = new FormControl();

  variableForm = new QuestionFormSourceVariable();
  title: string;

  private dialog = inject(MatDialog);
  private readonly data = inject<{ takenTokens: string[]; variable?: Question; optionsInUse?: Record<string, string[]> }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<SourceVariableDialogComponent>>(MatDialogRef);

  constructor() {
    this.title = this.data.variable ? 'Edit variable' : 'Add variable';

    if (this.data.variable) {
      this.variableForm.init(this.data.variable);
    }

    this.variableForm.setTakenTokens(this.data.takenTokens);
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('empathy-source-variable__dialog');
  }

  onAddValue(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value.length === 0) {
      return;
    }

    event.chipInput.clear();
    this.addValueCtrl.setValue(null);

    const { id } = GetNextId(this.variableForm.options.value as { id: string }[], 'opt');

    const newOption = new QuestionOptionForm();
    newOption.form.get('id').setValue(id);
    newOption.form.get('text').setValue(value);
    newOption.form.get('value').setValue(value);

    this.variableForm.options.push(newOption.form);
  }

  onRemoveValue(index: number): void {
    const optionId = this.variableForm.options.at(index).value.id;
    const affectedQuestions = this.data.optionsInUse?.[optionId] ?? [];

    if (affectedQuestions.length === 0) {
      this.variableForm.options.removeAt(index);
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Remove Option',
          confirmText: `The option removed is used as a conditional setting on <strong>${affectedQuestions.join(', ')}</strong>. If continue the condition/s will be removed.`,
          confirmBtn: 'Remove',
          color: 'warn'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.variableForm.options.removeAt(index);
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close(this.variableForm.form.value);
  }
}
